import type { DetailSuggestion } from "../index";
/**
 * Normalizes a query for matching: strips whitespace and uppercases.
 */
export const normalizeQuery = (s: string) =>
  (s ?? "").replace(/\s+/g, "").toUpperCase();
/**
 * Splits an address label on common separators (commas, pipes, slashes, dashes).
 * Empty parts are removed; trimming applied.
 */
 export function splitOnCommonSeparators(s: string): string[] {
   if (!s) return [];
   return s
     .split(/[;,|\u2013|\u2014]/g)
     .map(part => part.replace(/\s+/g, " ").trim())
     .filter(Boolean);
 }
/**
 * Attempts to find a trailing postcode at the end of the string (several locales).
 * If found, returns the prefix text and the matched postcode in uppercase.
 *
 * @returns { prefix: string; postcode: string } | null
 */
export const findTrailingPostcode = (text: string): { prefix: string; postcode: string } | null => {
  if (!text) return null;
  const patterns: RegExp[] = [
    /\b([A-Z]{1,2}\d{1,2}[A-Z]?)\s?(\d[A-Z]{2})\b$/i, // UK
    /\b\d{5}(?:-\d{4})?\b$/,                           // US
    /\b[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\s?\d[ABCEGHJ-NPRSTV-Z]\d\b$/i, // CA
    /\b\d{4}\s?[A-Z]{2}\b$/i,                          // NL
    /\b[A-Z]\d{2}\s?[A-Z0-9]{4}\b$/i,                  // IE (basic)
    /\b\d{5}\b$/,                                      // EU 5-digit
    /\b\d{4}\b$/,                                      // AU
  ];
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match && match.index != null) {
      const postcode = match[0].toUpperCase();
      const prefix = text.slice(0, match.index).trim();
      return { prefix, postcode };
    }
  }
  return null;
};

/**
 * Default “detail → multiline text” formatter.
 *
 * Priority:
 * 1) Use `detail.lines` if present.
 * 2) Else split label on common separators.
 * 3) Else isolate a trailing postcode onto its own line.
 * 4) Else return the raw label.
 *
 * @param detail The selected detail suggestion.
 * @returns Newline-separated address text.
 */
export const defaultFormatSelected = (detail: DetailSuggestion): string => {
  if (detail.lines?.length) return detail.lines.join("\n");

  const label = detail.label ?? "";
  const parts = splitOnCommonSeparators(label);
  if (parts.length > 1) return parts.join("\n");

  const tail = findTrailingPostcode(label);
  if (tail && tail.prefix) return `${tail.prefix}\n${tail.postcode}`;

  return label;
};

export const stripLeadingDuplicate = (code: string, label: string) => {
  const norm = (s: string) => s.replace(/[|,•]/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
  const nCode = norm(code);
  const nLabel = norm(label);
  return nLabel.startsWith(nCode)
    ? label.slice(label.toLowerCase().indexOf(code.toLowerCase()) + code.length).trim()
    : label;
};

// remove trailing "... 333 addresses"
export const stripTrailingCount = (s: string) =>
  s.replace(/\s*\b\d{1,6}\s+addresses?\b\.?$/i, "").trim()
